import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';

const MusicPlayerContext = createContext();

export const useMusicPlayer = () => {
  const context = useContext(MusicPlayerContext);
  if (!context) {
    // Return a default value instead of throwing to prevent crashes
    return {
      currentSong: null,
      isPlaying: false,
      volume: 1,
      currentTime: 0,
      duration: 0,
      playlist: [],
      currentIndex: 0,
      isRepeat: false,
      isShuffle: false,
      isYouTube: false,
      playSong: () => {},
      togglePlayPause: () => {},
      handleNext: () => {},
      handlePrevious: () => {},
      seekTo: () => {},
      setVolume: () => {},
      setRepeat: () => {},
      setShuffle: () => {},
      stop: () => {},
      setIsPlaying: () => {},
      setCurrentTime: () => {},
      setDuration: () => {},
    };
  }
  return context;
};

export const MusicPlayerProvider = ({ children }) => {
  const [currentSong, setCurrentSong] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolumeState] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playlist, setPlaylist] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isRepeat, setIsRepeat] = useState(false);
  const [isShuffle, setIsShuffle] = useState(false);
  const [wasPlayingBeforeTabHidden, setWasPlayingBeforeTabHidden] = useState(false);
  const [isYouTube, setIsYouTube] = useState(false);
  const audioRef = useRef(null);
  const handleNextRef = useRef(null);

  // playSong callback - defined early so it can be used in handleNext
  const playSong = useCallback((song, songList = []) => {
    if (!song) return;

    console.log('Playing song:', song.title);

    // Update current song first
    setCurrentSong(song);

    // Update playlist if provided
    if (songList.length > 0) {
      setPlaylist(songList);
      const index = songList.findIndex(s => s.id === song.id);
      setCurrentIndex(index >= 0 ? index : 0);
    }

    // Check if it's a YouTube song
    if (song.videoId) {
      setIsYouTube(true);
      setIsPlaying(true); // YouTube player will handle actual play
      console.log('YouTube video:', song.videoId);
    } else {
      // Audio file
      setIsYouTube(false);
      if (audioRef.current) {
        const audioUrl = song.audioUrl || song.url || song.audio;
        if (audioUrl) {
          audioRef.current.src = audioUrl;
          audioRef.current.play()
            .then(() => {
              setIsPlaying(true);
            })
            .catch((error) => {
              console.error('Error playing audio:', error);
              setIsPlaying(false);
            });
        } else {
          console.error('No audio URL found for song:', song);
          setIsPlaying(false);
        }
      }
    }
  }, []);

  // handleNext callback - uses ref to avoid re-initializing audio
  const handleNext = useCallback(() => {
    if (playlist.length === 0) return;

    let nextIndex;
    if (isShuffle) {
      nextIndex = Math.floor(Math.random() * playlist.length);
    } else {
      nextIndex = currentIndex === playlist.length - 1 ? 0 : currentIndex + 1;
    }

    setCurrentIndex(nextIndex);
    playSong(playlist[nextIndex], playlist);
  }, [currentIndex, playlist, isShuffle, playSong]);

  // Keep handleNextRef updated
  useEffect(() => {
    handleNextRef.current = handleNext;
  }, [handleNext]);

  // Initialize audio element
  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.volume = volume;

    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleDurationChange = () => {
      if (audio.duration && !isNaN(audio.duration)) {
        setDuration(audio.duration);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      if (isRepeat) {
        audio.currentTime = 0;
        audio.play().catch(console.error);
      } else if (handleNextRef.current) {
        handleNextRef.current();
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleError = (e) => {
      console.error('Audio error:', e);
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('error', handleError);
      audio.pause();
      audio.src = '';
    };
  }, [isRepeat, volume]);

  // Update volume
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = Math.max(0, Math.min(1, volume));
    }
  }, [volume]);

  // Handle tab visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setWasPlayingBeforeTabHidden(isPlaying);
      } else {
        if (wasPlayingBeforeTabHidden && currentSong && !isPlaying) {
          audioRef.current?.play().then(() => {
            setIsPlaying(true);
          }).catch(console.error);
        }
        setWasPlayingBeforeTabHidden(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isPlaying, currentSong, wasPlayingBeforeTabHidden]);

  const togglePlayPause = useCallback(() => {
    if (!currentSong) return;

    if (isYouTube) {
      // YouTube playback is controlled by MusicPlayer component
      // We just toggle our local state
      setIsPlaying(prev => !prev);
    } else {
      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
        } else {
          // Ensure src is set
          if (!audioRef.current.src) {
            const audioUrl = currentSong.audioUrl || currentSong.url || currentSong.audio;
            if (audioUrl) {
              audioRef.current.src = audioUrl;
            }
          }
          if (audioRef.current.src) {
            audioRef.current.play().catch(console.error);
          }
        }
      }
    }
  }, [currentSong, isPlaying, isYouTube]);

  const handlePrevious = useCallback(() => {
    if (playlist.length === 0) return;

    let prevIndex;
    if (isShuffle) {
      prevIndex = Math.floor(Math.random() * playlist.length);
    } else {
      prevIndex = currentIndex === 0 ? playlist.length - 1 : currentIndex - 1;
    }

    setCurrentIndex(prevIndex);
    playSong(playlist[prevIndex], playlist);
  }, [currentIndex, playlist, isShuffle, playSong]);

  const seekTo = useCallback((time) => {
    if (audioRef.current && !isYouTube) {
      audioRef.current.currentTime = Math.max(0, Math.min(time, duration));
      setCurrentTime(audioRef.current.currentTime);
    }
  }, [duration, isYouTube]);

  const setVolume = useCallback((newVolume) => {
    const clampedVolume = Math.max(0, Math.min(1, newVolume));
    setVolumeState(clampedVolume);
  }, []);

  const setRepeat = useCallback(() => {
    setIsRepeat(prev => !prev);
  }, []);

  const setShuffle = useCallback(() => {
    setIsShuffle(prev => !prev);
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setCurrentTime(0);
    setCurrentSong(null);
    setPlaylist([]);
    setCurrentIndex(0);
    setWasPlayingBeforeTabHidden(false);
  }, []);

  const value = {
    currentSong,
    isPlaying,
    volume,
    currentTime,
    duration,
    playlist,
    currentIndex,
    isRepeat,
    isShuffle,
    isYouTube,
    playSong,
    togglePlayPause,
    handleNext,
    handlePrevious,
    seekTo,
    setVolume,
    setRepeat,
    setShuffle,
    stop,
    setIsPlaying,
    setCurrentTime,
    setDuration,
  };

  return (
    <MusicPlayerContext.Provider value={value}>
      {children}
    </MusicPlayerContext.Provider>
  );
};
